import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DropdownComponent } from 'marketpro-design-lib';
import { DashboardFilter } from '../../../models/custom-dashboard.model';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { State } from '../../../../auth/models/state.model';
import { Region } from '../../../models/region-model';
import { DashboardStatisticsService } from '../../../services/dashboard-statistics.service';
import { DataFiltersService } from '../../../services/data-filters.service';
import { Gender } from '../../../models/gender.enum';
import { Currency } from '../../../models/currency.enum';
import { SpendingPer } from '../../../models/spending-per.enum';
import { DataLens } from '../../../models/data-lens.enum';

@Component({
  selector: 'mpro-dashboard-selector',
  templateUrl: './dashboard-selector.component.html',
  styleUrls: ['./dashboard-selector.component.scss'],
})
export class DashboardSelectorComponent implements OnDestroy {
  @ViewChild(DropdownComponent) public dropdown: DropdownComponent;

  public inEditCustomDashboard: DashboardFilter;

  addNew = false;
  public faSave = faSave;

  @Input() public inputValue: string = '';
  @Input() public systemDashboards: string[] = [];
  @Input() public userDashboards: DashboardFilter[] = [];

  @Output() public dashboardClicked: EventEmitter<string> = new EventEmitter();
  @Output() public dashboardChanged: EventEmitter<string> = new EventEmitter();

  private ngUnsubscribe: Subject<void> = new Subject();

  constructor(
    private dashboardService: DashboardStatisticsService,
    private dataFiltersService: DataFiltersService
  ) {}

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public onDashboardClicked(group: string) {
    this.dashboardClicked.emit(group);
    this.dropdown.dropdownOpen = false;
  }

  public onDropdownOpened() {
    this.dashboardChanged.emit();
  }

  onSave(dashboardFilter: DashboardFilter) {
    const currentDataFilters = this.dataFiltersService.getDataFiltersValue();
    // todo: check if that is safe operation
    const existingDashboard = this.userDashboards.find(
      (d) => d.name == dashboardFilter.name
    );

    let newCustomDashboard: DashboardFilter = {
      name: dashboardFilter.name,
      ageGroupId: currentDataFilters.ageGroup?.id,
      spendingGroupId: currentDataFilters.spendingGroup?.id,
      year: currentDataFilters.year,
      continentCodes: [],
    };

    if (currentDataFilters?.regionSelection.regions.size) {
      const region: Region = currentDataFilters?.regionSelection.regions
        .values()
        .next().value as Region;

      if (region.id) {
        newCustomDashboard.regionSelectionId = region.id;
      } else {
        newCustomDashboard.continentCodes = Array.from(
          currentDataFilters?.regionSelection.regions.values()
        ).map((region) => region.code);
      }
    } else if (currentDataFilters.regionSelection.countries.size) {
      // If no region is selected, only 1 country can be selected
      const countryCodes: string[] = Array.from(
        currentDataFilters.regionSelection.countries.values()
      ).map((country) => country.code);
      newCustomDashboard.world = new State();
      const a = countryCodes.map((code) => {
        return { code: new State() };
      });
      newCustomDashboard.world.childStates = countryCodes.reduce(
        (acc: { [key: string]: State }, code: string) => {
          return { ...acc, [code]: new State() };
        },
        {}
      );
    }

    if (currentDataFilters?.gender) {
      if (currentDataFilters.gender == 'male') {
        newCustomDashboard.gender = Gender.MALE;
      } else if (currentDataFilters.gender == 'female') {
        newCustomDashboard.gender = Gender.FEMALE;
      }
    }

    if (currentDataFilters?.currency) {
      if (currentDataFilters.currency == 0) {
        newCustomDashboard.currency = Currency.PPP_USD_2017;
      } else if (currentDataFilters.currency == 1) {
        newCustomDashboard.currency = 'NOMINAL' as Currency;
      }
    }

    if (currentDataFilters?.spendingPer) {
      if (currentDataFilters.spendingPer == 0) {
        newCustomDashboard.spendingPer = SpendingPer.DAY;
      } else if (currentDataFilters.spendingPer == 1) {
        newCustomDashboard.spendingPer = SpendingPer.YEAR;
      }
    }

    if (currentDataFilters?.dataLens) {
      switch (currentDataFilters.dataLens) {
        case 0:
          newCustomDashboard.dataLens = DataLens.NATIONAL;
          break;
        case 1:
          newCustomDashboard.dataLens = DataLens.SUBNATIONAL_PROVINCES;
          break;
        case 2:
          newCustomDashboard.dataLens = DataLens.SUBNATIONAL_CITIES;
          break;
        default:
          break;
      }
    }

    if (dashboardFilter.id) {
      newCustomDashboard.id = dashboardFilter.id;
      this.dashboardService
        .updateCustomDashboard(newCustomDashboard)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => this.closeCreateWindowAndReloadDashboards());
      return;
    }

    if (existingDashboard) {
      newCustomDashboard.id = existingDashboard.id;
      this.dashboardService
        .updateCustomDashboard(newCustomDashboard)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => this.closeCreateWindowAndReloadDashboards());
      return;
    }

    this.dashboardService
      .saveCustomDashboard(newCustomDashboard)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.closeCreateWindowAndReloadDashboards());
  }

  private closeCreateWindowAndReloadDashboards() {
    this.setAddNew(false);
    this.dashboardChanged.emit();
  }

  setAddNew(state: boolean) {
    const selectedDashboard = this.userDashboards.find(
      (d) => d.name == this.inputValue
    );
    if (selectedDashboard) {
      this.inEditCustomDashboard = {
        name: selectedDashboard.name,
        continentCodes: [],
      };
    } else {
      this.inEditCustomDashboard = { name: 'Title', continentCodes: [] };
    }
    this.addNew = state;
    this.dashboardChanged.emit();
    this.dropdown.dropdownOpen = true;
  }

  public onDeleteClicked(dashboardFilter: DashboardFilter) {
    if (dashboardFilter.id === undefined) {
      return;
    }

    this.dashboardService
      .deleteCustomDashboard(dashboardFilter.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.dashboardChanged.emit());
  }

  public onEditClicked(dashboardFilter: DashboardFilter) {
    this.setAddNew(true);
    this.inEditCustomDashboard = dashboardFilter;
  }
}
